import { useRef } from 'react';
import { BrandMark } from './BrandMark.js';
import { Icon } from './Icon.js';

export interface TopbarProps {
  page: 'planner' | 'status';
  compact?: boolean;
  onNavigate?: ((path: string) => void) | undefined;
  /** Runs before navigating home, so the planner can reset itself. */
  onHome?: () => void;
}

function follow(
  event: React.MouseEvent<HTMLAnchorElement>,
  path: string,
  onNavigate: TopbarProps['onNavigate'],
  before?: () => void,
): void {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
  if (before === undefined && onNavigate === undefined) return;
  event.preventDefault();
  before?.();
  onNavigate?.(path);
}

/** A quiet bar: the product name, what it is, and at most two ways out. */
export function Topbar({
  page,
  compact = false,
  onNavigate,
  onHome,
}: TopbarProps): React.JSX.Element {
  const about = useRef<HTMLDialogElement | null>(null);
  return (
    <header className="topbar">
      <a
        className="wordmark"
        href="/"
        aria-label="Mobilidade Lousada, início"
        onClick={(event) => follow(event, '/', page === 'planner' ? undefined : onNavigate, onHome)}
      >
        <BrandMark size={compact ? 32 : 38} />
        <span className="wordmark__text">
          Mobilidade Lousada
          {!compact && <span className="wordmark__sub">Mapa e transportes públicos</span>}
        </span>
      </a>
      <nav className="topnav" aria-label="Principal">
        <button
          type="button"
          className="topnav__link"
          aria-haspopup="dialog"
          onClick={() => about.current?.showModal()}
        >
          {compact ? <Icon name="info" size={20} /> : 'Sobre este mapa'}
          {compact && <span className="visually-hidden">Sobre este mapa</span>}
        </button>
        {page === 'status' ? (
          <a
            className="topnav__link topnav__link--action"
            href="/"
            onClick={(event) => follow(event, '/', onNavigate)}
          >
            Planear viagem
          </a>
        ) : (
          !compact && (
            <a
              className="topnav__link"
              href="/status"
              onClick={(event) => follow(event, '/status', onNavigate)}
            >
              Estado do serviço
            </a>
          )
        )}
      </nav>
      <dialog
        ref={about}
        className="about"
        aria-labelledby="about-title"
        onClick={(event) => {
          if (event.target === event.currentTarget) event.currentTarget.close();
        }}
      >
        <div className="about__body">
          <h2 id="about-title" className="about__title">
            Sobre este mapa
          </h2>
          <p className="about__lede">
            Reunimos num só lugar a informação de mobilidade do concelho de Lousada que hoje se
            encontra dispersa entre diferentes operadores e plataformas.
          </p>
          <p>
            Pode pesquisar uma rua, um local ou uma paragem, ver as próximas partidas e planear uma
            viagem de autocarro ou comboio com os trechos a pé incluídos.
          </p>
          <h3 className="about__subtitle">De onde vêm os dados</h3>
          <p>
            Horários publicados em dados abertos pelo Município de Lousada e pela CIM do Tâmega e
            Sousa (LINHAS). Ruas e locais do OpenStreetMap. São horários programados: não há
            informação em tempo real nem preços.
          </p>
          <h3 className="about__subtitle">Quem o faz</h3>
          <p>
            Um projeto da JSD Lousada, sem fins lucrativos e sem recolha de dados de utilização. Não
            é um serviço da Câmara Municipal de Lousada.
          </p>
          <form method="dialog" className="about__actions">
            <button type="submit" className="button button--ink">
              Fechar
            </button>
          </form>
        </div>
      </dialog>
    </header>
  );
}
